import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  formatPhoneNumber,
  parseApiResponse,
  userMessageForCode,
  stashPendingSignup,
  loadPendingSignup,
  saveCheckoutAttemptId,
  loadCheckoutAttemptId,
  getOrCreateCheckoutIdempotencyKey,
  markFreeBetaCheckoutResume,
  loadFreeBetaCheckoutResume
} from '../utils/checkoutClient';
import { fetchCsrfToken, authJsonHeaders, fetchSession } from '../utils/authClient';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 20px;
  padding: 3rem;
  width: 100%;
  max-width: 700px;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #00ff88;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.5);
`;

const Subtitle = styled.p`
  text-align: center;
  color: #cccccc;
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: #ffffff;
  font-weight: 500;
  font-size: 1rem;
`;

const Input = styled.input`
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #00ff88;
    box-shadow: 0 0 10px rgba(0, 255, 136, 0.3);
  }

  &::placeholder {
    color: #888888;
  }

  &.error {
    border-color: #ff4444;
    box-shadow: 0 0 10px rgba(255, 68, 68, 0.3);
  }
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  font-size: 0.9rem;
  margin-top: 0.25rem;
`;

const SubmitButton = styled.button`
  background: linear-gradient(45deg, #00ff88, #00cc6a);
  color: #000000;
  padding: 1rem;
  border: none;
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 255, 136, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const OrderSummary = styled.div`
  background: rgba(0, 255, 136, 0.1);
  border: 1px solid #00ff88;
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const SummaryTitle = styled.h3`
  color: #00ff88;
  margin-bottom: 1rem;
  font-size: 1.2rem;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  color: #cccccc;
`;

const SummaryTotal = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 255, 136, 0.3);
  font-weight: bold;
  color: #00ff88;
  font-size: 1.1rem;
`;

const InfoBox = styled.div`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid #00d4ff;
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 2rem;
  color: #00d4ff;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const TaxNotice = styled(InfoBox)`
  background: rgba(255, 193, 7, 0.12);
  border-color: #ffc107;
  color: #ffc107;
`;

function centsToDisplay(cents) {
  if (typeof cents !== 'number' || Number.isNaN(cents)) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

const PaymentForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const inFlight = useRef(false);

  const locationInmate = location.state?.inmateData || null;
  const locationNumber = location.state?.selectedNumber || null;
  const queryAttemptId = searchParams.get('checkoutAttemptId');
  const canceled = searchParams.get('canceled') === '1';

  const [inmateData, setInmateData] = useState(locationInmate);
  const [selectedNumber, setSelectedNumber] = useState(locationNumber);
  const [checkoutAttemptId, setCheckoutAttemptId] = useState(
    queryAttemptId || loadCheckoutAttemptId()
  );
  const [email, setEmail] = useState(locationInmate?.email || '');
  const [betaCode, setBetaCode] = useState('');
  const [pricing, setPricing] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Prefer location.state; recover phone/registration non-secrets from session when needed
    if (locationInmate && locationNumber?.phoneNumber) {
      stashPendingSignup(locationInmate, locationNumber);
      setInmateData(locationInmate);
      setSelectedNumber(locationNumber);
      setReady(true);
      return;
    }

    const pending = loadPendingSignup();
    if (checkoutAttemptId) {
      setReady(true);
      return;
    }
    if (pending?.phoneNumber && pending?.inmateConnectionId) {
      setInmateData({
        inmateConnectionId: pending.inmateConnectionId,
        inmateUuid: pending.inmateUuid,
        inmateId: pending.inmateId,
        inmateName: pending.inmateName,
        facilityName: pending.facilityName,
        state: pending.state,
        areaCode: pending.areaCode
      });
      setSelectedNumber({ phoneNumber: pending.phoneNumber, locality: pending.locality });
      setReady(true);
      return;
    }

    // Missing required signup payload (password never in session) — restart
    navigate('/inmate-signup');
  }, [locationInmate, locationNumber, checkoutAttemptId, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/checkout/pricing');
        const parsed = await parseApiResponse(res);
        if (cancelled) return;
        if (parsed.data?.code === 'SECURE_CHECKOUT_DISABLED') {
          // Controlled unavailability — no card fields, no legacy fallback, no retry loop
          setErrors({
            submit: userMessageForCode(parsed.data.code, parsed.data?.message)
          });
          return;
        }
        if (parsed.ok && parsed.data) {
          setPricing(parsed.data);
        }
      } catch {
        /* pricing display optional; server enforces price */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (canceled) {
      setErrors({
        submit: 'Checkout was canceled. You can try again — you will not be double-charged for an incomplete session.'
      });
    }
  }, [canceled]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inFlight.current || isSubmitting) return;

    setErrors({});
    const emailTrim = (email || '').trim();
    if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      setErrors({ email: 'Please enter a valid email address', submit: '' });
      return;
    }

    // Resume existing attempt: session cookie + CSRF only.
    if (checkoutAttemptId && (!inmateData?.inmateConnectionId || !selectedNumber?.phoneNumber)) {
      inFlight.current = true;
      setIsSubmitting(true);
      try {
        const session = await fetchSession();
        if (!session.authenticated) {
          setErrors({
            submit: userMessageForCode(
              session.code || 'UNAUTHENTICATED',
              session.message
            )
          });
          return;
        }
        const csrfToken = session.csrfToken || (await fetchCsrfToken());
        const trimmedBeta = (betaCode || '').trim();
        const useFreeBeta = !!trimmedBeta || loadFreeBetaCheckoutResume();
        const res = await fetch(useFreeBeta ? '/api/checkout/free-beta' : '/api/checkout/create', {
          method: 'POST',
          credentials: 'include',
          headers: authJsonHeaders(csrfToken),
          body: JSON.stringify({
            checkoutAttemptId,
            ...(trimmedBeta ? { betaCode: trimmedBeta } : {})
          })
        });
        const parsed = await parseApiResponse(res);
        await handleCreateResult(res, parsed, useFreeBeta);
      } catch {
        setErrors({
          submit:
            'Unable to reach the server. If you already completed payment on Stripe, open the success page with your checkout reference instead of paying again.'
        });
      } finally {
        inFlight.current = false;
        setIsSubmitting(false);
      }
      return;
    }

    if (!inmateData?.inmateConnectionId || !selectedNumber?.phoneNumber) {
      setErrors({ submit: 'Registration details are incomplete. Please start signup again.' });
      navigate('/inmate-signup');
      return;
    }

    inFlight.current = true;
    setIsSubmitting(true);

    try {
      const session = await fetchSession();
      if (!session.authenticated) {
        setErrors({
          submit: userMessageForCode(session.code || 'UNAUTHENTICATED', session.message)
        });
        return;
      }
      const csrfToken = session.csrfToken || (await fetchCsrfToken());
      const idempotencyKey = getOrCreateCheckoutIdempotencyKey();

      const payload = {
        inmateConnectionId: inmateData.inmateConnectionId,
        selectedPhoneNumber: selectedNumber.phoneNumber,
        contactEmail: emailTrim || undefined,
        idempotencyKey
      };

      const trimmedBeta = (betaCode || '').trim();
      const useFreeBeta = !!trimmedBeta;
      if (useFreeBeta) {
        payload.betaCode = trimmedBeta;
        markFreeBetaCheckoutResume(true);
      } else {
        markFreeBetaCheckoutResume(false);
      }

      const res = await fetch(useFreeBeta ? '/api/checkout/free-beta' : '/api/checkout/create', {
        method: 'POST',
        credentials: 'include',
        headers: authJsonHeaders(csrfToken),
        body: JSON.stringify(payload)
      });
      const parsed = await parseApiResponse(res);
      await handleCreateResult(res, parsed, useFreeBeta);
    } catch {
      setErrors({
        submit:
          'Network error before we could confirm checkout setup. Do not assume payment failed — check your email/Stripe or support if charged.'
      });
    } finally {
      inFlight.current = false;
      setIsSubmitting(false);
    }
  };

  async function handleCreateResult(res, parsed, usedBetaCode = false) {
    if (res.status === 429) {
      const retryAfter = res.headers.get('Retry-After');
      setErrors({
        submit: userMessageForCode('RATE_LIMITED') +
          (retryAfter ? ` Retry after ${retryAfter} seconds.` : '')
      });
      return;
    }

    if (parsed.parseError === 'json_parse_failed' || parsed.parseError === 'html_or_non_json_error') {
      if (res.status >= 500) {
        setErrors({
          submit: userMessageForCode('INTERNAL_ERROR')
        });
      } else {
        setErrors({ submit: 'Unexpected server response. Please try again.' });
      }
      return;
    }

    const data = parsed.data || {};
    if (parsed.ok && data.success) {
      if (data.checkoutAttemptId) {
        saveCheckoutAttemptId(data.checkoutAttemptId);
        setCheckoutAttemptId(data.checkoutAttemptId);
      }
      if (data.alreadyProvisioned || data.freeBeta || usedBetaCode) {
        if (data.freeBeta || usedBetaCode) {
          markFreeBetaCheckoutResume(true);
        }
        navigate(
          `/payment-success?checkoutAttemptId=${encodeURIComponent(data.checkoutAttemptId)}`
        );
        return;
      }
      if (data.checkoutUrl) {
        markFreeBetaCheckoutResume(false);
        window.location.assign(data.checkoutUrl);
        return;
      }
      setErrors({ submit: userMessageForCode(data.code, 'Checkout could not be started.') });
      return;
    }

    const code = data.code || 'INTERNAL_ERROR';
    if (
      code === 'UNAUTHENTICATED' ||
      code === 'UNAUTHORIZED' ||
      code === 'CHECKOUT_RESUME_AUTH_REQUIRED' ||
      code === 'LEGACY_CHECKOUT_INELIGIBLE'
    ) {
      setErrors({ submit: userMessageForCode(code, data.message) });
      return;
    }
    if (
      code === 'PENDING_OR_INACTIVE' ||
      code === 'INMATE_FORBIDDEN' ||
      code === 'CSRF_REJECTED' ||
      code === 'FREE_BETA_DISABLED' ||
      code === 'BETA_CODE_REQUIRED' ||
      code === 'BETA_CODE_INVALID' ||
      code === 'BETA_CODE_EXPIRED' ||
      code === 'BETA_CODE_ALREADY_USED'
    ) {
      setErrors({ submit: userMessageForCode(code, data.message) });
      return;
    }
    if (res.status === 401 || res.status === 403) {
      setErrors({ submit: userMessageForCode(code, data.message) });
      return;
    }
    if (code === 'NUMBER_UNAVAILABLE' || code === 'NUMBER_ALREADY_ASSIGNED') {
      setErrors({ submit: userMessageForCode(code) });
      setTimeout(() => {
        navigate('/phone-selection', { state: { inmateData } });
      }, 2500);
      return;
    }
    setErrors({ submit: userMessageForCode(code, data.message) });
  }

  if (!ready) {
    return (
      <Container>
        <Card>
          <Title>Loading checkout…</Title>
        </Card>
      </Container>
    );
  }

  const displayName = inmateData?.inmateName || 'your account';
  const phoneDisplay = formatPhoneNumber(
    selectedNumber?.phoneNumber ||
      (checkoutAttemptId ? null : null)
  );
  const totalLabel = pricing ? centsToDisplay(pricing.totalCents) : centsToDisplay(999);
  const serviceLabel = pricing ? centsToDisplay(pricing.amountCents) : centsToDisplay(999);
  const hasBetaCode = !!(betaCode || '').trim();

  return (
    <Container>
      <Card>
        <Title>Complete Payment</Title>
        <Subtitle>Monthly Skylink number service for {displayName}</Subtitle>

        <OrderSummary>
          <SummaryTitle>Order Summary</SummaryTitle>
          {phoneDisplay ? (
            <SummaryRow>
              <span>Phone Number:</span>
              <span>{phoneDisplay}</span>
            </SummaryRow>
          ) : null}
          <SummaryRow>
            <span>Monthly Service:</span>
            <span>{serviceLabel}</span>
          </SummaryRow>
          <SummaryRow>
            <span>Tax:</span>
            <span>{pricing ? centsToDisplay(pricing.taxCents) : '$0.00'}</span>
          </SummaryRow>
          <SummaryTotal>
            <span>Total due today:</span>
            <span>{totalLabel}</span>
          </SummaryTotal>
        </OrderSummary>

        <TaxNotice>
          <strong>Tax (test configuration):</strong> Tax is currently $0.00 under an explicit
          test-only zero-tax strategy. Production tax (Stripe Automatic Tax or professional
          guidance) is a launch blocker and is not charged here.
        </TaxNotice>

        <InfoBox>
          <strong>Secure payment:</strong> Card details are entered only on Stripe’s hosted
          checkout page. Skylink never stores your card number or CVV. You will be redirected to
          Stripe to complete a monthly subscription. If you have a one-time beta code, enter it
          below to skip Stripe for this signup.
        </InfoBox>

        {errors.submit && (
          <ErrorMessage
            role="alert"
            style={{ textAlign: 'center', marginBottom: '1rem' }}
            id="checkout-submit-error"
          >
            {errors.submit}
          </ErrorMessage>
        )}

        <Form onSubmit={handleSubmit} noValidate>
          <FormGroup>
            <Label htmlFor="email">Receipt email (optional)</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
                if (errors.submit) setErrors((prev) => ({ ...prev, submit: '' }));
              }}
              className={errors.email ? 'error' : ''}
              placeholder="you@example.com"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
              autoComplete="email"
            />
            {errors.email && (
              <ErrorMessage id="email-error" role="alert">
                {errors.email}
              </ErrorMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="betaCode">Beta code (optional)</Label>
            <Input
              type="text"
              id="betaCode"
              name="betaCode"
              value={betaCode}
              onChange={(e) => {
                setBetaCode(e.target.value);
                if (errors.submit) setErrors((prev) => ({ ...prev, submit: '' }));
              }}
              placeholder="Enter one-time beta code if you have one"
              autoComplete="off"
              spellCheck={false}
            />
          </FormGroup>

          <SubmitButton
            type="submit"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
          >
            {isSubmitting
              ? hasBetaCode
                ? 'Activating beta signup…'
                : 'Redirecting to secure checkout…'
              : hasBetaCode
                ? 'Redeem beta code'
                : `Continue to Stripe — ${totalLabel}/mo`}
          </SubmitButton>
        </Form>
      </Card>
    </Container>
  );
};

export default PaymentForm;
